import {
  CheckCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FileExclamationOutlined,
  PauseCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { Alert, Button, Card, List, message, Modal, Progress, Space, Spin, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { downloadTaskResult, getFailedFiles, getTaskStatus } from '../services/api';
import './ProcessStatus.less';

const { Title, Text } = Typography;

/**
 * 简化版的ProcessStatus组件
 * 专注于核心功能：任务状态轮询、进度条显示和下载按钮
 */
const ProcessStatus = ({ taskId, onStatusChange, onDownload }) => {
  const [status, setStatus] = useState('pending');
  const [progress, setProgress] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [processedFiles, setProcessedFiles] = useState(0);
  const [failedFiles, setFailedFiles] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [canStart, setCanStart] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [filesCount, setFilesCount] = useState({ total: 0, pending: 0, processing: 0, completed: 0, failed: 0 });
  
  // 失败文件相关状态
  const [failedFilesList, setFailedFilesList] = useState([]);
  const [showFailedFiles, setShowFailedFiles] = useState(false);
  const [failedFilesModalVisible, setFailedFilesModalVisible] = useState(false);
  const [isLoadingFailedFiles, setIsLoadingFailedFiles] = useState(false);

  // 轮询任务状态
  useEffect(() => {
    if (taskId) {
      // 首次加载
      checkTaskStatus();
      
      // 如果状态是processing，开始轮询
      if (status === 'processing') {
        startPolling();
      }
    }
    
    return () => {
      setIsPolling(false);
    };
  }, [taskId, status]);

  const checkTaskStatus = async () => {
    try {
      const data = await getTaskStatus(taskId);
      
      // 更新状态 - 增加进度稳定性
      setStatus(data.status);
      
      // 进度更新逻辑：正确显示后端计算的进度
      const newProgress = data.progress || 0;
      const currentProgress = progress;
      
      // 如果任务已完成，直接显示正确的进度
      if (data.status === 'completed' || data.status === 'failed') {
        setProgress(newProgress);
      } else if (data.status === 'processing') {
        // 处理中时，直接显示后端计算的正确进度
        setProgress(newProgress);
      } else {
        setProgress(newProgress);
      }
      
      setTotalFiles(data.total_files || 0);
      setProcessedFiles(data.processed_files || 0);
      setFailedFiles(data.failed_files || 0);
      setErrorMessage(data.error_message || '');
      setCanStart(data.can_start_processing || false);
      setCanDownload(data.can_download_excel || false);
      setTaskName(data.task_name || '');
      setFilesCount(data.files_count || { total: 0, pending: 0, processing: 0, completed: 0, failed: 0 });
      
      // 如果有失败的文件，获取失败文件列表
      if (data.failed_files > 0 && data.status === 'completed') {
        setShowFailedFiles(true);
        // 延迟加载失败文件列表，避免频繁请求
        if (failedFilesList.length === 0) {
          loadFailedFiles();
        }
      } else {
        setShowFailedFiles(false);
      }
      
      // 通知父组件状态变化
      if (onStatusChange) {
        onStatusChange(data);
      }
      
      // 如果任务正在处理且没有在轮询，开始轮询
      if (data.status === 'processing' && !isPolling) {
        startPolling();
      }
      
      // 如果任务完成，停止轮询
      if (data.status === 'completed' || data.status === 'failed') {
        setIsPolling(false);
      }
      
    } catch (error) {
      console.error('获取任务状态失败:', error);
      setErrorMessage(error.message || '获取状态失败');
      setIsPolling(false);
    }
  };

  const startPolling = () => {
    if (isPolling) return;
    
    setIsPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const data = await getTaskStatus(taskId);
        
        // 更新状态 - 增加进度稳定性
        setStatus(data.status);
        
        // 进度更新逻辑：正确显示后端计算的进度
        const newProgress = data.progress || 0;
        const currentProgress = progress;
        
        // 如果任务已完成，直接显示正确的进度
        if (data.status === 'completed' || data.status === 'failed') {
          setProgress(newProgress);
        } else if (data.status === 'processing') {
          // 处理中时，直接显示后端计算的正确进度
          setProgress(newProgress);
        } else {
          setProgress(newProgress);
        }
        
        setTotalFiles(data.total_files || 0);
        setProcessedFiles(data.processed_files || 0);
        setFailedFiles(data.failed_files || 0);
        setErrorMessage(data.error_message || '');
        setCanStart(data.can_start_processing || false);
        setCanDownload(data.can_download_excel || false);
        setTaskName(data.task_name || '');
        setFilesCount(data.files_count || { total: 0, pending: 0, processing: 0, completed: 0, failed: 0 });
        
        // 通知父组件状态变化
        if (onStatusChange) {
          onStatusChange(data);
        }
        
        // 如果任务完成，停止轮询
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollInterval);
          setIsPolling(false);
          
          if (data.status === 'completed') {
            message.success('任务处理完成！');
          } else {
            message.error('任务处理失败');
          }
        }
      } catch (error) {
        console.error('轮询状态失败:', error);
        clearInterval(pollInterval);
        setIsPolling(false);
        setErrorMessage(error.message || '获取状态失败');
      }
    }, 3000); // 每3秒检查一次
  };

  const handleDownload = async () => {
    try {
      console.log('📥 开始下载结果:', taskId)
      
      const result = await downloadTaskResult(taskId);
      message.success(`文件下载成功: ${result.filename}`);
      
      // 通知父组件下载事件
      if (onDownload) {
        onDownload(result);
      }
      
    } catch (error) {
      console.error('下载失败:', error);
      message.error(error.message || '下载失败');
    }
  };

  const handleRefresh = () => {
    checkTaskStatus();
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'pending':
        return <PauseCircleOutlined style={{ color: '#1890ff' }} />;
      case 'processing':
        return <Spin size="small" />;
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <PauseCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending':
        return '等待开始';
      case 'processing':
        return '处理中';
      case 'completed':
        return '已完成';
      case 'failed':
        return '处理失败';
      default:
        return '未知状态';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'pending':
        return 'blue';
      case 'processing':
        return 'orange';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'default';
    }
  };

  // 加载失败文件列表
  const loadFailedFiles = async () => {
    try {
      setIsLoadingFailedFiles(true);
      const data = await getFailedFiles(taskId);
      setFailedFilesList(data.failed_files || []);
    } catch (error) {
      console.error('获取失败文件列表失败:', error);
      message.error('获取失败文件列表失败');
    } finally {
      setIsLoadingFailedFiles(false);
    }
  };
  
  // 显示失败文件详情
  const showFailedFilesDetail = () => {
    setFailedFilesModalVisible(true);
  };

  if (!taskId) {
    return (
      <Card className="process-status-card">
        <Alert
          message="请先选择或创建任务"
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card 
      className="process-status-card"
      title={
        <Space>
          {getStatusIcon()}
          <span>任务状态: {getStatusText()}</span>
        </Space>
      }
      extra={
        <Button 
          type="text" 
          icon={<ReloadOutlined />} 
          onClick={handleRefresh}
          loading={isPolling}
        >
          刷新
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* 任务基本信息 */}
        <div className="task-info">
          <Title level={5}>任务ID: {taskId}</Title>
          {taskName && <Text type="secondary">任务名称: {taskName}</Text>}
        </div>

        {/* 进度条 */}
        <div className="progress-section">
          <Progress
            percent={Math.round(progress)}
            status={
              status === 'failed' 
                ? 'exception' 
                : (status === 'completed' ? 'success' : 'active')
            }
            strokeColor={
              status === 'processing' ? '#1890ff' : '#52c41a'  // 处理中蓝色，其他都是绿色
            }
          />
          <Space className="progress-info" direction="vertical" size="small">
            <Space>
              <Text>总文件: {totalFiles}</Text>
              <Text>已处理: {processedFiles}</Text>
              {failedFiles > 0 && <Text type="danger">失败: {failedFiles}</Text>}
            </Space>
            
            {/* 任务完成状态提示 */}
            {status === 'completed' && (
              <div>
                {failedFiles === 0 ? (
                  <Text type="success">
                    ✅ 全部文件处理成功！({processedFiles}/{totalFiles})
                  </Text>
                ) : processedFiles === 0 ? (
                  <Text type="danger">
                    ❌ 全部文件处理失败！({failedFiles}/{totalFiles})
                  </Text>
                ) : (
                  <Text type="warning">
                    ⚠️ 部分文件处理失败（{processedFiles}/{totalFiles} 成功，{failedFiles} 失败）
                  </Text>
                )}
              </div>
            )}
            
            {/* 处理中状态提示 */}
            {status === 'processing' && (
              <Text type="secondary">
                🔄 正在处理中...
              </Text>
            )}
          </Space>
        </div>

        {/* 文件统计 */}
        {filesCount.total > 0 && (
          <div className="files-count">
            <Title level={5}>文件统计</Title>
            <Space wrap>
              <Text>总数: {filesCount.total}</Text>
              <Text>等待: {filesCount.pending}</Text>
              <Text>处理中: {filesCount.processing}</Text>
              <Text>已完成: {filesCount.completed}</Text>
              {filesCount.failed > 0 && <Text type="danger">失败: {filesCount.failed}</Text>}
            </Space>
          </div>
        )}

        {/* 错误信息 */}
        {errorMessage && (
          <Alert
            message="处理错误"
            description={errorMessage}
            type="error"
            showIcon
            closable
          />
        )}

        {/* 操作按钮 */}
        <Space className="action-buttons">
          {canDownload && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              size="large"
            >
              下载结果
            </Button>
          )}
          
          {/* 失败文件列表 */}
          {showFailedFiles && (
            <Button
              type="default"
              icon={<FileExclamationOutlined />}
              onClick={showFailedFilesDetail}
              size="large"
              loading={isLoadingFailedFiles}
            >
              查看失败文件 ({failedFiles}个)
            </Button>
          )}
        </Space>
      </Space>
      
      {/* 失败文件详情Modal */}
      <Modal
        title={
          <Space>
            <FileExclamationOutlined style={{ color: '#ff4d4f' }} />
            <span>处理失败的文件列表</span>
          </Space>
        }
        open={failedFilesModalVisible}
        onCancel={() => setFailedFilesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setFailedFilesModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            以下文件在处理过程中遇到错误，请检查文件格式或重新上传。
          </Text>
        </div>
        
        <List
          dataSource={failedFilesList}
          loading={isLoadingFailedFiles}
          renderItem={(file) => (
            <List.Item
              style={{ 
                padding: '12px 16px', 
                border: '1px solid #ffccc7', 
                borderRadius: '6px', 
                marginBottom: '8px',
                backgroundColor: '#fff2f0'
              }}
            >
              <List.Item.Meta
                avatar={
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: '#ffccc7',
                    borderRadius: '6px'
                  }}>
                    <FileExclamationOutlined style={{ fontSize: '18px', color: '#ff4d4f' }} />
                  </div>
                }
                title={
                  <div>
                    <Text strong>{file.file_name}</Text>
                    <Tag color="red" size="small" style={{ marginLeft: '8px' }}>
                      处理失败
                    </Tag>
                  </div>
                }
                description={
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {file.error_message && (
                      <div style={{ color: '#ff4d4f', marginTop: '4px' }}>
                        错误信息: {file.error_message}
                      </div>
                    )}
                    {file.processing_time && (
                      <div style={{ marginTop: '4px' }}>
                        处理时间: {file.processing_time}秒
                      </div>
                    )}
                    {file.updated_at && (
                      <div style={{ marginTop: '4px' }}>
                        更新时间: {new Date(file.updated_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
          locale={{
            emptyText: '暂无失败文件'
          }}
        />
      </Modal>
    </Card>
  );
};

export default ProcessStatus; 